import { useEffect, useState, useContext } from "react";
import { Box, Typography, Grid, Card, CardContent, Stack, Chip, Sheet, Button, Accordion, AccordionGroup, AccordionSummary, AccordionDetails, LinearProgress, CircularProgress } from "@mui/joy";
import { LayoutContext } from "../context";
import { APIGetUserStatistics } from "../api";
import ActivityHeatmap from "../components/ActivityHeatmap";
import CardTest from "../components/ui/CardTest";
import { CaretDown, Folder } from "@phosphor-icons/react";



export default function StatsPage() {
    const { token, tests, loadUserTests, loadingTests, setGenerateTestModalOpen } = useContext(LayoutContext);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStatistics();
        loadUserTests();
    }, []);

    const loadStatistics = async () => {
        try {
            setLoading(true);
            const response = await APIGetUserStatistics(token);
            if (response.success) {
                setStatistics(response.statistics);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateTest = () => {
        setGenerateTestModalOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex' }}>
                <Box
                    component="main"
                    className="MainContent"
                    sx={{
                        px: { xs: 2, md: 6 },
                        pt: 2,
                        pb: { xs: 2, sm: 2, md: 3 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        gap: 1,
                    }}
                >
                    <Typography level="h2" component="h1">
                        Статистика
                    </Typography>
                    <Typography>Загрузка статистики...</Typography>
                </Box>
            </Box>
        );
    }

    const { user_stats, category_stats, global_stats } = statistics || {};

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 3,
                }}
            >

                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Статистика
                    </Typography>
                </Box>

                {/* Тепловая карта активности */}
                <ActivityHeatmap token={token} />

                <Grid container spacing={3}>
                    {/* Основная статистика пользователя */}
                    <Grid xs={12} md={6}>
                        <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
                            <CardContent>
                                <Typography level="h4" sx={{ mb: 3 }}>
                                    Ваша статистика
                                </Typography>

                                {user_stats && (
                                    <Stack spacing={3}>
                                        {/* Круговая диаграмма с общим процентом */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                <CircularProgress
                                                    determinate
                                                    value={user_stats.accuracy_percentage || 0}
                                                    size="lg"
                                                    thickness={8}
                                                    color={
                                                        user_stats.accuracy_percentage > 64 ? "success" :
                                                        user_stats.accuracy_percentage >= 45 ? "warning" : "danger"
                                                    }
                                                    sx={{ '--CircularProgress-size': '140px' }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0, left: 0, bottom: 0, right: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexDirection: 'column'
                                                    }}
                                                >
                                                    <Typography level="h2" fontWeight="bold">
                                                        {user_stats.accuracy_percentage}%
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        точность
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Всего тестов:</Typography>
                                            <Typography fontWeight="bold">{user_stats.total_tests}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Завершено тестов:</Typography>
                                            <Typography fontWeight="bold" color="primary">
                                                {user_stats.completed_tests} ({user_stats.completion_percentage}%)
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Отвечено вопросов:</Typography>
                                            <Typography fontWeight="bold">{user_stats.total_questions_answered}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Правильных ответов:</Typography>
                                            <Typography fontWeight="bold" color="success">
                                                {user_stats.correct_answers} ({user_stats.accuracy_percentage}%)
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Пропущено вопросов:</Typography>
                                            <Typography fontWeight="bold">{user_stats.skipped_questions}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Среднее в день:</Typography>
                                            <Typography fontWeight="bold">
                                                {user_stats.daily_averages.tests_per_day} тестов, {user_stats.daily_averages.questions_per_day} вопросов
                                            </Typography>
                                        </Stack>
                                        </Stack>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Статистика по категориям */}
                    {category_stats && category_stats.length > 0 && (
                        <Grid xs={12}>
                            <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
                                <CardContent>
                                    <Typography level="h4" sx={{ mb: 3 }}>
                                        Статистика по категориям
                                    </Typography>

                                    <AccordionGroup sx={{ gap: 1 }}>
                                        {category_stats.map((group) => (
                                            <Accordion
                                                key={group.root_id}
                                                variant="outlined"
                                                sx={{ borderRadius: 'lg' }}
                                            >
                                                <AccordionSummary
                                                    indicator={<CaretDown size={20} />}
                                                    sx={{ borderRadius: 'lg' }}
                                                >
                                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                                        <Folder size={20} />
                                                        <Typography level="title-md" sx={{ flex: 1 }}>
                                                            {group.root_name}
                                                        </Typography>
                                                        <Chip
                                                            size="sm"
                                                            color={
                                                                group.accuracy_percentage > 64 ? "success" :
                                                                group.accuracy_percentage >= 45 ? "warning" : "danger"
                                                            }
                                                        >
                                                            {group.accuracy_percentage}%
                                                        </Chip>
                                                        <Typography level="body-sm" color="neutral">
                                                            {group.correct_answers}/{group.total_questions}
                                                        </Typography>
                                                    </Stack>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                                                        {group.topics.map((topic) => (
                                                            <Sheet
                                                                key={topic.category_id}
                                                                variant="soft"
                                                                sx={{
                                                                    p: 1.5,
                                                                    borderRadius: 'md',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 2
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography level="body-sm" fontWeight={500}>
                                                                        {topic.category_name}
                                                                    </Typography>
                                                                    <LinearProgress
                                                                        determinate
                                                                        value={topic.accuracy_percentage}
                                                                        color={
                                                                            topic.accuracy_percentage > 64 ? "success" :
                                                                            topic.accuracy_percentage >= 45 ? "warning" : "danger"
                                                                        }
                                                                        sx={{ mt: 0.5, height: 6, borderRadius: 'sm' }}
                                                                    />
                                                                </Box>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="soft"
                                                                    color={
                                                                        topic.accuracy_percentage >= 80 ? "success" :
                                                                        topic.accuracy_percentage >= 60 ? "warning" : "danger"
                                                                    }
                                                                >
                                                                    {topic.accuracy_percentage}%
                                                                </Chip>
                                                                <Typography level="body-xs" color="neutral" sx={{ minWidth: 45, textAlign: 'right' }}>
                                                                    {topic.correct_answers}/{topic.total_questions}
                                                                </Typography>
                                                            </Sheet>
                                                        ))}
                                                    </Stack>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </AccordionGroup>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Мои тесты */}
                <Box sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            mb: 2,
                            gap: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'start', sm: 'center' },
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography level="h3">
                            Мои тесты
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleGenerateTest}
                        >
                            Создать тест
                        </Button>
                    </Box>

                    {loadingTests ? (
                        <Typography>Загрузка тестов...</Typography>
                    ) : tests.length > 0 ? (
                        <Grid container spacing={2} columns={{ xs: 2, sm: 12, md: 12 }}>
                            {tests.map((test) => (
                                <Grid key={test.id} xs={12} sm={6} lg={4}>
                                    <CardTest test={test} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Sheet variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 'lg' }}>
                            <Typography level="h4" color="neutral" sx={{ mb: 2 }}>
                                У вас пока нет тестов
                            </Typography>
                            <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
                                Создайте свой первый тест, чтобы начать обучение
                            </Typography>
                            <Button size="lg" onClick={handleGenerateTest}>
                                Создать первый тест
                            </Button>
                        </Sheet>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
